// Admin-side console for the Founder Edge OS.
//
// Single page lists every company with a founder portal. Selecting one opens
// a panel with: Advisor Thread (admin can post as Advisor or Sia), Claims
// (admin can publish-override), Weekly Drafts (admin can approve and publish).
import { useEffect, useState } from "react";
import { MessagesSquare, ShieldCheck, FileEdit, Send, ChevronRight, Sparkles, Building2, Video, Hash, X, Plus, RefreshCw } from "lucide-react";
import { ChetanClipsGrid } from "@/components/chetan-clips-grid";

const BASE = import.meta.env.BASE_URL;
const auth = (): Record<string, string> => ({ "x-admin-token": localStorage.getItem("admin_token") || "" });

interface CompanyRow { id: number; name: string; sector: string | null; pendingClaims: number; totalClaims: number; pendingUpdates: number; }
interface Snapshot {
  company: { id: number; name: string; sector: string | null; mediaHashtags: string[] };
  founders: Array<{ id: number; name: string; email: string }>;
  thread: { id: number; advisorName: string | null; advisorAdminId: number | null; messages: any[] } | null;
  claims: any[];
  drafts: any[];
}

function fmt(iso: string) { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }

export default function AdminFounderPortal() {
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [tab, setTab] = useState<"thread" | "claims" | "updates" | "clips">("thread");
  const [reply, setReply] = useState("");
  const [authorKind, setAuthorKind] = useState<"advisor" | "sia">("advisor");
  const [busy, setBusy] = useState(false);

  async function loadCompanies() {
    const r = await fetch(`${BASE}api/admin/founder-portal/companies`, { headers: auth() });
    if (r.ok) setCompanies(await r.json());
  }
  async function loadSnap(id: number) {
    const r = await fetch(`${BASE}api/admin/founder-portal/companies/${id}`, { headers: auth() });
    if (r.ok) setSnap(await r.json());
  }
  useEffect(() => { loadCompanies(); }, []);
  useEffect(() => { if (selected) loadSnap(selected); }, [selected]);

  async function postReply() {
    if (!selected || !reply.trim() || busy) return;
    setBusy(true);
    try {
      await fetch(`${BASE}api/admin/founder-portal/companies/${selected}/thread/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ body: reply.trim(), authorKind }),
      });
      setReply("");
      await loadSnap(selected);
    } finally { setBusy(false); }
  }
  async function publishClaim(id: number) {
    if (!confirm("Publish this claim to the founder's investor page. The founder will be emailed.")) return;
    setBusy(true);
    try {
      await fetch(`${BASE}api/admin/founder-portal/claims/${id}/publish`, { method: "POST", headers: auth() });
      if (selected) await loadSnap(selected);
      await loadCompanies();
    } finally { setBusy(false); }
  }
  async function approveDraft(id: number) {
    setBusy(true);
    try {
      await fetch(`${BASE}api/admin/founder-portal/weekly-drafts/${id}/approve`, { method: "POST", headers: auth() });
      if (selected) await loadSnap(selected);
      await loadCompanies();
    } finally { setBusy(false); }
  }
  async function publishDraft(id: number) {
    setBusy(true);
    try {
      await fetch(`${BASE}api/admin/founder-portal/weekly-drafts/${id}/publish`, { method: "POST", headers: auth() });
      if (selected) await loadSnap(selected);
      await loadCompanies();
    } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="border-b border-white/[0.08] px-6 py-4">
        <h1 className="text-lg font-bold">Founder Edge OS · Admin Console</h1>
        <p className="text-xs text-white/50 mt-0.5">Reply in threads. Publish claims. Approve and publish weekly updates.</p>
      </div>

      <div className="grid grid-cols-12 gap-0" style={{ minHeight: "calc(100vh - 65px)" }}>
        {/* Companies list */}
        <div className="col-span-3 border-r border-white/[0.08] overflow-y-auto" style={{ maxHeight: "calc(100vh - 65px)" }}>
          {companies.map(c => (
            <button key={c.id} onClick={() => setSelected(c.id)}
              data-testid={`row-company-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-white/[0.05] hover:bg-white/[0.04] ${selected === c.id ? "bg-[#FF5C00]/10 border-l-2 border-l-[#FF5C00]" : ""}`}>
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="w-3 h-3 text-white/40" />
                <span className="text-sm font-semibold truncate">{c.name}</span>
                <ChevronRight className="w-3 h-3 text-white/30 ml-auto" />
              </div>
              <div className="flex gap-2 text-[10px] font-mono">
                {c.pendingClaims > 0 && <span className="bg-[#FF5C00]/15 text-[#FF5C00] rounded px-1.5">{c.pendingClaims} claims</span>}
                {c.pendingUpdates > 0 && <span className="bg-amber-500/15 text-amber-300 rounded px-1.5">{c.pendingUpdates} updates</span>}
                {c.pendingClaims === 0 && c.pendingUpdates === 0 && <span className="text-white/35">all clear</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Detail panel */}
        <div className="col-span-9 overflow-y-auto" style={{ maxHeight: "calc(100vh - 65px)" }}>
          {!snap ? (
            <div className="p-12 text-sm text-white/40">Pick a company on the left.</div>
          ) : (
            <div className="p-6 max-w-4xl">
              <div className="mb-4">
                <h2 className="text-xl font-bold">{snap.company.name}</h2>
                <div className="text-xs text-white/55 mt-0.5">
                  {snap.company.sector && <>{snap.company.sector} · </>}
                  Founders: {snap.founders.map(f => f.email).join(", ") || "none"}
                </div>
              </div>
              <div className="flex gap-1 mb-5 border-b border-white/[0.08]">
                {[
                  { k: "thread" as const, l: "Thread", icon: MessagesSquare },
                  { k: "claims" as const, l: "Claims", icon: ShieldCheck },
                  { k: "updates" as const, l: "Updates", icon: FileEdit },
                  { k: "clips" as const, l: "Clips", icon: Video },
                ].map(t => {
                  const Icon = t.icon;
                  return (
                    <button key={t.k} onClick={() => setTab(t.k)}
                      className={`px-3 py-2 text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 border-b-2 ${tab === t.k ? "border-[#FF5C00] text-[#FF5C00]" : "border-transparent text-white/55 hover:text-white"}`}>
                      <Icon className="w-3 h-3" /> {t.l}
                    </button>
                  );
                })}
              </div>

              {tab === "thread" && (
                <div>
                  <div className="bg-black/30 border border-white/[0.08] rounded-lg p-4 max-h-96 overflow-y-auto space-y-2 mb-3">
                    {snap.thread?.messages.length ? snap.thread.messages.map((m: any) => (
                      <div key={m.id} className="text-xs">
                        <span className="font-bold">{m.authorName}</span>
                        <span className="text-white/40"> · {m.authorKind} · {fmt(m.createdAt)}</span>
                        <div className="text-white/85 mt-0.5 whitespace-pre-wrap">{m.body}</div>
                      </div>
                    )) : <div className="text-white/40 text-xs">No messages yet.</div>}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <select value={authorKind} onChange={e => setAuthorKind(e.target.value as any)}
                      className="bg-black/40 border border-white/[0.12] rounded text-xs px-2 py-1.5">
                      <option value="advisor">Post as Advisor</option>
                      <option value="sia">Post as Sia</option>
                    </select>
                    {snap.thread?.advisorName && <span className="text-[10px] text-white/45">Current Advisor: {snap.thread.advisorName}</span>}
                  </div>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={5000}
                    placeholder="Type your reply. The founders will be emailed."
                    data-testid="input-admin-thread-reply"
                    className="w-full bg-black/40 border border-white/[0.12] rounded px-3 py-2 text-sm outline-none focus:border-[#FF5C00]/50 resize-none mb-2" />
                  <button onClick={postReply} disabled={busy || !reply.trim()}
                    data-testid="button-admin-thread-send"
                    className="inline-flex items-center gap-1.5 bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-xs font-semibold rounded px-3 py-1.5">
                    <Send className="w-3 h-3" /> {busy ? "Sending" : "Send"}
                  </button>
                </div>
              )}

              {tab === "claims" && (
                <div className="space-y-2">
                  {snap.claims.length === 0 ? <div className="text-xs text-white/40">No claims yet.</div> : snap.claims.map((c: any) => (
                    <div key={c.id} className="bg-white/[0.04] border border-white/[0.08] rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-wider text-white/55">{c.fieldLabel}</span>
                        <span className={`text-[10px] font-mono uppercase rounded px-2 py-0.5 ${
                          c.status === "pending" ? "bg-white/[0.06] text-white/55" :
                          c.status === "accepted" ? "bg-green-500/10 text-green-300" :
                          c.status === "corrected" ? "bg-[#FF5C00]/10 text-[#FF5C00]" :
                          c.status === "rejected" ? "bg-red-500/10 text-red-300" :
                          "bg-amber-500/10 text-amber-300"}`}>{c.status}</span>
                      </div>
                      <p className="text-xs text-white/85 mb-2">{c.correctedValue || c.proposedValue}</p>
                      {(c.status === "pending" || c.status === "rejected") && (
                        <button onClick={() => publishClaim(c.id)} disabled={busy}
                          data-testid={`button-admin-publish-claim-${c.id}`}
                          className="text-[11px] text-amber-300 hover:text-amber-200 font-medium">
                          Publish to investor view (override)
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {tab === "clips" && (
                <div className="space-y-5">
                  <MediaHashtagsEditor
                    companyId={snap.company.id}
                    initial={snap.company.mediaHashtags || []}
                    onSaved={() => loadSnap(snap.company.id)}
                  />
                  <ChetanClipsGrid
                    endpoint={`api/admin/founder-portal/companies/${snap.company.id}/clips`}
                    authHeader={{ "x-admin-token": localStorage.getItem("admin_token") || "" }}
                    tone="dark"
                    hideWhenEmpty={false}
                  />
                </div>
              )}

              {tab === "updates" && (
                <div className="space-y-2">
                  {snap.drafts.length === 0 ? <div className="text-xs text-white/40">No drafts yet.</div> : snap.drafts.map((d: any) => (
                    <div key={d.id} className="bg-white/[0.04] border border-white/[0.08] rounded p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold">Week of {d.weekOf}</span>
                        <span className="text-[10px] font-mono uppercase text-white/55">{d.status}</span>
                      </div>
                      {d.draftBody && <pre className="text-[11px] text-white/75 whitespace-pre-wrap font-sans leading-relaxed mb-2 max-h-40 overflow-y-auto">{d.draftBody}</pre>}
                      <div className="flex gap-2">
                        {d.status === "founder_approved" && (
                          <button onClick={() => approveDraft(d.id)} disabled={busy}
                            data-testid={`button-admin-approve-draft-${d.id}`}
                            className="text-[11px] bg-[#FF5C00]/15 hover:bg-[#FF5C00]/25 text-[#FF5C00] font-medium px-2 py-1 rounded">
                            Approve as Advisor
                          </button>
                        )}
                        {d.status === "advisor_approved" && (
                          <button onClick={() => publishDraft(d.id)} disabled={busy}
                            data-testid={`button-admin-publish-draft-${d.id}`}
                            className="text-[11px] bg-green-500/15 hover:bg-green-500/25 text-green-300 font-medium px-2 py-1 rounded">
                            Publish to investor page
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Per-company media hashtag whitelist. PC adds tags like "Crezam" or
// "SahilCEO" (no #). Riya uses these as the matching key when scanning Shubh
// Labh's nightly clip catalog. Saving PATCHes /api/companies/:id; "Sync now"
// triggers a fresh Riya sweep so PC sees results without waiting for 10pm IST.
function MediaHashtagsEditor({
  companyId, initial, onSaved,
}: { companyId: number; initial: string[]; onSaved: () => void }) {
  const [tags, setTags] = useState<string[]>(initial);
  const [input, setInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => { setTags(initial); }, [initial.join(",")]);

  function addTag() {
    const cleaned = input.trim().replace(/^#/, "").toLowerCase();
    if (!cleaned) return;
    if (tags.includes(cleaned)) { setInput(""); return; }
    setTags([...tags, cleaned]);
    setInput("");
  }
  function removeTag(t: string) { setTags(tags.filter((x) => x !== t)); }

  async function save() {
    setSaving(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}api/companies/${companyId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ mediaHashtags: tags }),
      });
      if (!r.ok) throw new Error(await r.text());
      setMsg("Saved.");
      onSaved();
    } catch (e: any) { setMsg(`Save failed: ${e.message}`); }
    finally { setSaving(false); setTimeout(() => setMsg(null), 4000); }
  }

  async function syncNow() {
    setSyncing(true); setMsg(null);
    try {
      const r = await fetch(`${BASE}api/admin/founder-portal/riya/run`, {
        method: "POST", headers: auth(),
      });
      if (!r.ok) throw new Error(await r.text());
      setMsg("Riya sweep started. Refresh in 30 seconds to see new clips.");
    } catch (e: any) { setMsg(`Sync failed: ${e.message}`); }
    finally { setSyncing(false); setTimeout(() => setMsg(null), 8000); }
  }

  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Hash className="w-3.5 h-3.5 text-[#FF5C00]" />
        <span className="text-xs font-mono uppercase tracking-wider text-white/75">Media hashtag whitelist</span>
      </div>
      <p className="text-[11px] text-white/55 mb-3">
        Riya nightly cron matches these tags against every clip on Shubh Labh.
        No # needed. Example: Crezam, NovaPay, SahilCEO.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {tags.length === 0 && <span className="text-[11px] text-white/35 italic">No tags set. Riya will fall back to matching the company name.</span>}
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 bg-[#FF5C00]/15 text-[#FF5C00] text-xs font-mono rounded px-2 py-0.5">
            #{t}
            <button onClick={() => removeTag(t)} className="hover:text-white" data-testid={`button-remove-tag-${t}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
          placeholder="Type a tag and press Enter"
          data-testid="input-media-hashtag"
          className="flex-1 bg-black/40 border border-white/[0.12] rounded px-3 py-1.5 text-xs outline-none focus:border-[#FF5C00]/50"
        />
        <button onClick={addTag} disabled={!input.trim()}
          className="inline-flex items-center gap-1 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-40 text-white text-xs font-semibold rounded px-3 py-1.5"
          data-testid="button-add-tag">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={save} disabled={saving}
          className="bg-[#FF5C00] hover:bg-[#FF5C00]/90 disabled:opacity-40 text-white text-xs font-semibold rounded px-3 py-1.5"
          data-testid="button-save-tags">
          {saving ? "Saving" : "Save tags"}
        </button>
        <button onClick={syncNow} disabled={syncing}
          className="inline-flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/[0.10] disabled:opacity-40 text-white/85 text-xs font-semibold rounded px-3 py-1.5"
          data-testid="button-sync-now">
          <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing" : "Sync now"}
        </button>
        {msg && <span className="text-[11px] text-white/65">{msg}</span>}
      </div>
    </div>
  );
}
